import React from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";

interface IFilterBarProps {
  children: React.ReactNode;
  onSearch: () => void;
  onClear?: () => void;
  /** Number of filters currently set — drives the badge and the Clear button. */
  activeCount?: number;
  /** Line under the buttons, e.g. "Showing 50 merchants on page 1". */
  summary?: React.ReactNode;
  /** Extra controls on the button row, such as a sort dropdown. */
  trailing?: React.ReactNode;
  defaultOpen?: boolean;
  searchLabel?: string;
}

/**
 * The standard filter panel: one collapsible card, a grid of fields, and a
 * single Search / Clear row. Collapsed it reads as one line, so a page with
 * ten filters no longer pushes its results below the fold.
 */
export const FilterBar: React.FC<IFilterBarProps> = ({
  children,
  onSearch,
  onClear,
  activeCount = 0,
  summary,
  trailing,
  defaultOpen = true,
  searchLabel = "Search",
}) => {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <Card className="mb-3">
      <Card.Header
        className="d-flex justify-content-between align-items-center py-2"
        role="button"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="fw-semibold small">
          <i className={`bi bi-chevron-${open ? "down" : "right"} me-2`} />
          Filters
          {activeCount > 0 && (
            <Badge bg="primary" className="ms-2">
              {activeCount} active
            </Badge>
          )}
        </span>
        {summary && <span className="text-muted small">{summary}</span>}
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body className="py-3">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                onSearch();
              }}
            >
              <Row className="g-3">{children}</Row>
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <Button type="submit" variant="primary" size="sm">
                  <i className="bi bi-search me-1" />
                  {searchLabel}
                </Button>
                {onClear && activeCount > 0 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={onClear}
                  >
                    <i className="bi bi-x-circle me-1" />
                    Clear all
                  </Button>
                )}
                {trailing && <div className="ms-auto">{trailing}</div>}
              </div>
            </Form>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

interface IFilterFieldProps {
  label: string;
  /** Says what the filter actually matches, for anyone meeting it first time. */
  hint?: string;
  /** Same job as `hint`, but on an ⓘ — for panels where the rows must stay tight. */
  tooltip?: string;
  children: React.ReactNode;
  md?: number;
  lg?: number;
}

export const FilterField: React.FC<IFilterFieldProps> = ({
  label,
  hint,
  tooltip,
  children,
  md = 4,
  lg,
}) => (
  <Col md={md} lg={lg}>
    <Form.Group>
      <Form.Label className="small fw-semibold mb-1">
        {label}
        {tooltip && (
          <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <span className="text-muted ms-1">ⓘ</span>
          </OverlayTrigger>
        )}
      </Form.Label>
      {children}
      {hint && (
        <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
          {hint}
        </Form.Text>
      )}
    </Form.Group>
  </Col>
);
